/**
 * Stage 0: Article Setup — thesis configuration.
 *
 * Shows as the first stage in the pipeline. Editable before the article
 * starts running, read-only after any stage has completed.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useArticleContext } from '../ArticleContext';
import { blogRunsService } from '../../../services/blogRunsService';
import { blogCandidatesService } from '../../../services/blogCandidatesService';
import type { ArticleDocument } from '../types';

interface PipelineDefaults {
  default_business_context: string;
  content_pillars: string[];   // loose "Category" buckets
  clusters?: string[];          // tight hub-and-spoke groups
  default_image_style?: string;
}

export function StageSetupV2({ wordpressCategories }: { wordpressCategories: { name: string; slug: string; wp_id: number }[] }) {
  const { candidate, run, document, refreshDocument, refreshArticles } = useArticleContext();

  const [defaults, setDefaults] = useState<PipelineDefaults | null>(null);

  // Load pipeline defaults
  useEffect(() => {
    blogRunsService.getPipelineDefaults()
      .then(setDefaults)
      .catch(() => {});
  }, []);

  // Has any stage completed? If so, this is read-only.
  const hasStarted = (run?.stages || []).some(
    (s: { status: string }) => s.status === 'succeeded'
  );

  if (hasStarted) {
    return <ReadOnlySetup candidate={candidate} document={document} />;
  }

  return (
    <EditableSetup
      candidate={candidate}
      defaults={defaults}
      wordpressCategories={wordpressCategories}
    />
  );
}

// ---------------------------------------------------------------------------
// Read-only view (after article has started)
// ---------------------------------------------------------------------------

function ReadOnlySetup({ candidate, document }: { candidate: any; document: ArticleDocument | null }) {
  if (!candidate) return null;
  const thesis = document?.thesis || candidate;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Article Setup</h3>
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <Field label="Article name" value={thesis.candidate_name} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="ICP" value={thesis.icp} />
          <Field label="Funnel stage" value={thesis.funnel_stage} />
          <Field label="Focus" value={thesis.focus} />
          <Field label="Category" value={thesis.content_pillar} />
          <Field label="Cluster" value={candidate.cluster} />
          <Field label="WordPress category" value={thesis.wordpress_category?.name || candidate.wordpress_category?.name} />
        </div>
        {thesis.business_context && <Field label="Business context" value={thesis.business_context} />}
        {thesis.directional_guidance && <Field label="Directional guidance" value={thesis.directional_guidance} />}
      </div>
      <div className="text-xs text-gray-400 italic">Article setup is locked after the pipeline starts.</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm text-gray-800">{value || '—'}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editable view (before article starts)
// ---------------------------------------------------------------------------

function EditableSetup({
  candidate,
  defaults,
  wordpressCategories,
}: {
  candidate: any;
  defaults: PipelineDefaults | null;
  wordpressCategories: { name: string; slug: string; wp_id: number }[];
}) {
  const { run, refreshDocument, refreshArticles } = useArticleContext();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    candidate_name: candidate?.candidate_name || '',
    icp: candidate?.icp || '',
    funnel_stage: candidate?.funnel_stage || 'Top',
    focus: candidate?.focus || 'B2B',
    wordpress_category_slug: candidate?.wordpress_category?.slug || '',
    content_pillar: candidate?.content_pillar || '',
    cluster: candidate?.cluster || '',
    business_context: candidate?.business_context || '',
    directional_guidance: candidate?.directional_guidance || '',
  });

  // Candidates (for deriving the selected cluster's pillar article + published warning).
  const [allCandidates, setAllCandidates] = useState<any[]>([]);
  useEffect(() => {
    blogCandidatesService.list().then(r => setAllCandidates(r.candidates || [])).catch(() => {});
  }, []);
  const clusterPillar = form.cluster
    ? allCandidates.find(c => (c.cluster || '') === form.cluster && c.pillar && c.candidate_id !== candidate?.candidate_id)
    : undefined;

  // Apply defaults on first load
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (defaults && !defaultsApplied) {
      setForm(prev => ({
        ...prev,
        business_context: prev.business_context || defaults.default_business_context || '',
      }));
      setDefaultsApplied(true);
    }
  }, [defaults, defaultsApplied]);

  const handleSave = useCallback(async () => {
    if (!candidate?.candidate_id) return;
    setSaving(true);
    setSaved(false);
    try {
      // Resolve the WordPress category object from the slug
      const wpCat = wordpressCategories.find(c => c.slug === form.wordpress_category_slug.trim());

      await blogCandidatesService.update(candidate.candidate_id, {
        candidate_name: form.candidate_name.trim(),
        icp: form.icp.trim(),
        funnel_stage: form.funnel_stage as any,
        focus: form.focus as any,
        wordpress_category_slug: form.wordpress_category_slug.trim(),
        content_pillar: form.content_pillar.trim() || undefined,
        cluster: form.cluster.trim() || undefined,
        business_context: form.business_context.trim() || undefined,
        directional_guidance: form.directional_guidance.trim() || undefined,
      });

      // Also update the document's thesis section so stages read correct data
      if (run?.run_id) {
        await blogRunsService.updateDocument(run.run_id, {
          thesis: {
            candidate_name: form.candidate_name.trim(),
            icp: form.icp.trim(),
            funnel_stage: form.funnel_stage,
            focus: form.focus,
            content_pillar: form.content_pillar.trim() || '',
            wordpress_category: wpCat || undefined,
            business_context: form.business_context.trim() || '',
            directional_guidance: form.directional_guidance.trim() || '',
          },
        });
        await refreshDocument();
      }

      setSaved(true);
      await refreshArticles();
    } catch (e: any) {
      console.error('Failed to save setup:', e);
    } finally {
      setSaving(false);
    }
  }, [candidate?.candidate_id, form, run?.run_id, refreshDocument, refreshArticles, wordpressCategories]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Article Setup</h3>
      <p className="text-xs text-gray-500">Configure the article details below. These will be used throughout the pipeline.</p>

      <div className="space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <label className="block text-xs text-gray-600">
            Article name <span className="text-red-400">*</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.candidate_name}
              onChange={(e) => setForm(prev => ({ ...prev, candidate_name: e.target.value }))}
              required
            />
          </label>
          <label className="block text-xs text-gray-600">
            ICP <span className="text-red-400">*</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.icp}
              onChange={(e) => setForm(prev => ({ ...prev, icp: e.target.value }))}
              required
            />
          </label>
          <label className="block text-xs text-gray-600">
            Funnel stage
            <select
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.funnel_stage}
              onChange={(e) => setForm(prev => ({ ...prev, funnel_stage: e.target.value }))}
            >
              <option value="Top">Top</option>
              <option value="Medium">Medium</option>
              <option value="Bottom">Bottom</option>
              <option value="Authority">Authority</option>
            </select>
          </label>
          <label className="block text-xs text-gray-600">
            Focus
            <select
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.focus}
              onChange={(e) => setForm(prev => ({ ...prev, focus: e.target.value }))}
            >
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
          </label>
          <label className="block text-xs text-gray-600">
            WordPress category <span className="text-red-400">*</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.wordpress_category_slug}
              onChange={(e) => setForm(prev => ({ ...prev, wordpress_category_slug: e.target.value }))}
              required
            >
              <option value="">Select a category</option>
              {wordpressCategories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600">
            Category
            <select
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.content_pillar}
              onChange={(e) => setForm(prev => ({ ...prev, content_pillar: e.target.value }))}
            >
              <option value="">Select a category</option>
              {(defaults?.content_pillars || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600">
            Cluster <span className="text-gray-400">(optional — join a hub-and-spoke group)</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.cluster}
              onChange={(e) => setForm(prev => ({ ...prev, cluster: e.target.value }))}
            >
              <option value="">Not in a cluster</option>
              {(defaults?.clusters || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {form.cluster && (
              <div className="mt-1 space-y-1">
                {clusterPillar ? (
                  <div className="text-[11px] text-gray-500">Pillar article: <span className="text-gray-700">{clusterPillar.candidate_name}</span></div>
                ) : (
                  <div className="text-[11px] text-gray-400">No pillar set for this cluster yet — designate one after building.</div>
                )}
                {clusterPillar?.published_confirmed_at && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Heads up: this cluster's pillar is already published. Building this article will need a follow-up to update the published pillar with a link back to it.
                  </div>
                )}
              </div>
            )}
          </label>
        </div>
        <label className="block text-xs text-gray-600">
          Business context
          <textarea
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            rows={2}
            value={form.business_context}
            onChange={(e) => setForm(prev => ({ ...prev, business_context: e.target.value }))}
          />
        </label>
        <label className="block text-xs text-gray-600">
          Directional guidance
          <div className="text-[11px] text-gray-400 mt-0.5">Give the pipeline direction on what kind of article to write — angle, tone, audience emphasis, etc.</div>
          <textarea
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            rows={2}
            value={form.directional_guidance}
            onChange={(e) => setForm(prev => ({ ...prev, directional_guidance: e.target.value }))}
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium rounded-md text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#1a2332' }}
            disabled={saving || !form.candidate_name.trim() || !form.icp.trim()}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && <span className="text-xs text-green-600">Saved</span>}
        </div>
      </div>
    </div>
  );
}
